import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Divider,
  Paper,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SendIcon from '@mui/icons-material/Send';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import color from '../../components/utils/Colors';
import { useSnackbar  } from '../../components/utils/SnackBar';

interface Message {
  id: string;
  sender: string;
  avatar: string;
  lastMessage: string;
  timestamp: string;
  unread: boolean;
  isOnline: boolean;
}

interface ChatMessage {
  id: string;
  sender: string;
  content: string;
  timestamp: string;
  type: 'sent' | 'received';
}

const Messages: React.FC = () => {
  const showSnackbar = useSnackbar();
  const isMobile = useMediaQuery('(max-width:600px)');
  const [conversations, setConversations] = useState<Message[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<string | null>(null);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [messageInput, setMessageInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedForDelete, setSelectedForDelete] = useState<string | null>(null);

  // Mock data
  React.useEffect(() => {
    const mockConversations: Message[] = [
      {
        id: '1',
        sender: 'Sarah Anderson',
        avatar: 'SA',
        lastMessage: 'Thanks for the update! See you in the next class.',
        timestamp: '2 min ago',
        unread: true,
        isOnline: true,
      },
      {
        id: '2',
        sender: 'John Smith',
        avatar: 'JS',
        lastMessage: 'The assignment looks good. Let me review it.',
        timestamp: '1 hour ago',
        unread: false,
        isOnline: true,
      },
      {
        id: '3',
        sender: 'Mike Johnson',
        avatar: 'MJ',
        lastMessage: 'Can you help me with the React project?',
        timestamp: '3 hours ago',
        unread: true,
        isOnline: false,
      },
      {
        id: '4',
        sender: 'Emma Wilson',
        avatar: 'EW',
        lastMessage: 'Perfect! I learned a lot from your explanation.',
        timestamp: 'Yesterday',
        unread: false,
        isOnline: false,
      },
      {
        id: '5',
        sender: 'David Lee',
        avatar: 'DL',
        lastMessage: 'Let\'s connect on LinkedIn after the course.',
        timestamp: '2 days ago',
        unread: false,
        isOnline: true,
      },
    ];

    const mockChatMessages: ChatMessage[] = [
      {
        id: '1',
        sender: 'Sarah Anderson',
        content: 'Hi! How are you doing with the TypeScript course?',
        timestamp: '10:30 AM',
        type: 'received',
      },
      {
        id: '2',
        sender: 'You',
        content: 'Great! I\'m on lesson 5 now. Really enjoying it.',
        timestamp: '10:32 AM',
        type: 'sent',
      },
      {
        id: '3',
        sender: 'Sarah Anderson',
        content: 'That\'s awesome! Do you have any questions?',
        timestamp: '10:33 AM',
        type: 'received',
      },
      {
        id: '4',
        sender: 'You',
        content: 'Actually yes, can you clarify the difference between interfaces and types?',
        timestamp: '10:35 AM',
        type: 'sent',
      },
      {
        id: '5',
        sender: 'Sarah Anderson',
        content: 'Sure! Interfaces are used for object shapes and can be extended. Types are more flexible and can represent any type...',
        timestamp: '10:36 AM',
        type: 'received',
      },
      {
        id: '6',
        sender: 'Sarah Anderson',
        content: 'Thanks for the update! See you in the next class.',
        timestamp: '10:38 AM',
        type: 'received',
      },
    ];

    setConversations(mockConversations);
    if (mockConversations.length > 0) {
      setSelectedConversation(mockConversations[0].id);
      setChatMessages(mockChatMessages);
    }
  }, []);

  const filteredConversations = conversations.filter((conv) =>
    conv.sender.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSendMessage = () => {
    if (!messageInput.trim()) {
      showSnackbar('Please enter a message');
      return;
    }

    const newMessage: ChatMessage = {
      id: String(chatMessages.length + 1),
      sender: 'You',
      content: messageInput,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      type: 'sent',
    };

    setChatMessages([...chatMessages, newMessage]);
    setMessageInput('');
    showSnackbar('Message sent');
  };

  const handleDeleteConversation = () => {
    if (selectedForDelete) {
      setConversations(conversations.filter((c) => c.id !== selectedForDelete));
      if (selectedConversation === selectedForDelete) {
        const remaining = conversations.filter((c) => c.id !== selectedForDelete);
        setSelectedConversation(remaining.length > 0 ? remaining[0].id : null);
        setChatMessages([]);
      }
      showSnackbar('Conversation deleted');
    }
    setDeleteDialogOpen(false);
    setSelectedForDelete(null);
  };

  return (
    <Box sx={{ minHeight: '100vh', background: 'transparent', paddingY: '40px' }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ marginBottom: '30px' }}>
          <Typography
            variant="h4"
            sx={{
              color: color.textColor,
              fontWeight: 'bold',
              marginBottom: '8px',
            }}
          >
            Messages
          </Typography>
          <Typography variant="body1" sx={{ color: '#666' }}>
            Stay connected with your instructors and classmates
          </Typography>
        </Box>

        {/* Main Chat Layout */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '350px 1fr',
            gap: '20px',
            minHeight: '600px',
          }}
        >
          {/* Conversations List */}
          <Card sx={{ boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.08)', borderRadius: '12px', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ padding: '16px', borderBottom: `1px solid #f0f0f0` }}>
              <TextField
                fullWidth
                placeholder="Search conversations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size="small"
                className="input-root2"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#999' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </CardContent>

            {/* Conversations */}
            <Box sx={{ flex: 1, overflowY: 'auto' }}>
              {filteredConversations.length > 0 ? (
                <List sx={{ padding: 0 }}>
                  {filteredConversations.map((conversation, index) => (
                    <React.Fragment key={conversation.id}>
                      <ListItemButton
                        selected={selectedConversation === conversation.id}
                        onClick={() => {
                          setSelectedConversation(conversation.id);
                          // In real app, fetch messages for this conversation
                        }}
                        sx={{
                          padding: '12px 16px',
                          background:
                            selectedConversation === conversation.id
                              ? color.secondColor
                              : 'transparent',
                          '&:hover': {
                            background:
                              selectedConversation === conversation.id
                                ? color.secondColor
                                : '#f5f5f5',
                          },
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar
                            sx={{
                              background: color.firstColor,
                              color: 'white',
                              fontWeight: 'bold',
                              position: 'relative',
                            }}
                          >
                            {conversation.avatar}
                            {conversation.isOnline && (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  bottom: '0',
                                  right: '0',
                                  width: '12px',
                                  height: '12px',
                                  background: '#4caf50',
                                  borderRadius: '50%',
                                  border: '2px solid white',
                                }}
                              />
                            )}
                          </Avatar>
                        </ListItemAvatar>

                        <ListItemText
                          primary={
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: conversation.unread ? 'bold' : '400',
                                color: color.textColor,
                              }}
                            >
                              {conversation.sender}
                            </Typography>
                          }
                          secondary={
                            <Typography
                              variant="caption"
                              sx={{
                                color: conversation.unread ? color.firstColor : '#999',
                                fontWeight: conversation.unread ? '500' : '400',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {conversation.lastMessage}
                            </Typography>
                          }
                        />

                        {conversation.unread && (
                          <Box
                            sx={{
                              width: '10px',
                              height: '10px',
                              background: color.firstColor,
                              borderRadius: '50%',
                              marginLeft: '8px',
                            }}
                          />
                        )}
                      </ListItemButton>
                      {index < filteredConversations.length - 1 && (
                        <Divider sx={{ margin: 0 }} />
                      )}
                    </React.Fragment>
                  ))}
                </List>
              ) : searchQuery ? (
                <Box sx={{ padding: '20px', textAlign: 'center' }}>
                  <Typography sx={{ color: '#999' }}>No conversations found</Typography>
                </Box>
              ) : (
                <Box sx={{ padding: '20px', textAlign: 'center' }}>
                  <Typography sx={{ color: '#999' }}>No conversations yet</Typography>
                </Box>
              )}
            </Box>
          </Card>

          {/* Chat Area */}
          {selectedConversation ? (
            <Card sx={{ boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.08)', borderRadius: '12px', display: 'flex', flexDirection: 'column' }}>
              {/* Chat Header */}
              <CardContent
                sx={{
                  padding: '16px',
                  borderBottom: `1px solid #f0f0f0`,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <Avatar
                    sx={{
                      background: color.firstColor,
                      color: 'white',
                      fontWeight: 'bold',
                    }}
                  >
                    {conversations.find((c) => c.id === selectedConversation)?.avatar}
                  </Avatar>
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 'bold', color: color.textColor }}>
                      {conversations.find((c) => c.id === selectedConversation)?.sender}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#999' }}>
                      {conversations.find((c) => c.id === selectedConversation)?.isOnline
                        ? 'Online'
                        : 'Offline'}
                    </Typography>
                  </Box>
                </Box>

                <IconButton
                  onClick={() => {
                    setSelectedForDelete(selectedConversation);
                    setDeleteDialogOpen(true);
                  }}
                >
                  <DeleteIcon sx={{ color: '#999' }} />
                </IconButton>
              </CardContent>

              {/* Messages */}
              <Box
                sx={{
                  flex: 1,
                  overflowY: 'auto',
                  padding: '20px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px',
                }}
              >
                {chatMessages.length > 0 ? (
                  chatMessages.map((msg) => (
                    <Box
                      key={msg.id}
                      sx={{
                        display: 'flex',
                        justifyContent: msg.type === 'sent' ? 'flex-end' : 'flex-start',
                      }}
                    >
                      <Paper
                        sx={{
                          padding: '12px 16px',
                          background: msg.type === 'sent' ? color.firstColor : '#f0f0f0',
                          color: msg.type === 'sent' ? 'white' : color.textColor,
                          maxWidth: '70%',
                          borderRadius: msg.type === 'sent' ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                        }}
                      >
                        <Typography variant="body2" sx={{ marginBottom: '4px' }}>
                          {msg.content}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            opacity: 0.7,
                            display: 'block',
                            textAlign: msg.type === 'sent' ? 'right' : 'left',
                          }}
                        >
                          {msg.timestamp}
                        </Typography>
                      </Paper>
                    </Box>
                  ))
                ) : (
                  <Box sx={{ textAlign: 'center', padding: '20px' }}>
                    <Typography sx={{ color: '#999' }}>
                      No messages yet. Start the conversation!
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Message Input */}
              <Box
                sx={{
                  padding: '16px',
                  borderTop: `1px solid #f0f0f0`,
                  display: 'flex',
                  gap: '8px',
                }}
              >
                <TextField
                  fullWidth
                  placeholder="Type your message..."
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleSendMessage();
                    }
                  }}
                  className="input-root2"
                  size="small"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" sx={{ color: '#999' }}>
                          <AttachFileIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  variant="contained"
                  sx={{
                    background: color.firstColor,
                    color: 'white',
                    minWidth: '50px',
                    '&:hover': {
                      background: color.forthColor,
                    },
                  }}
                  onClick={handleSendMessage}
                >
                  <SendIcon />
                </Button>
              </Box>
            </Card>
          ) : (
            <Card sx={{ boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.08)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography sx={{ color: '#999' }}>
                  Select a conversation to start messaging
                </Typography>
              </CardContent>
            </Card>
          )}
        </Box>
      </Container>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle sx={{ color: color.textColor, fontWeight: 'bold' }}>
          Delete Conversation
        </DialogTitle>
        <DialogContent sx={{ paddingY: '20px' }}>
          <Typography sx={{ color: '#666' }}>
            Are you sure you want to delete this conversation? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: '20px' }}>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ color: '#999' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConversation}
            sx={{ textTransform: 'none' }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Messages;